import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/kielo";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [progress, setProgress] = useState(null);

  const refreshProgress = useCallback(async () => {
    try {
      const { data } = await api.get("/progress");
      setProgress(data);
    } catch (e) {
      setProgress(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshProgress();
    } catch (e) {
      setUser(null);
    } finally {
      setLoadingAuth(false);
    }
  }, [refreshProgress]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoadingAuth(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setUser(null);
    setProgress(null);
    window.location.href = "/";
  };

  const setSession = async (userData) => {
    setUser(userData);
    await refreshProgress();
    setLoadingAuth(false);
  };

  const completeLesson = async (lessonId) => {
    const { data } = await api.post("/progress/complete-lesson", { lesson_id: lessonId });
    setProgress(data);
    return data;
  };

  const saveQuizResult = async (lessonId, score, total) => {
    const { data } = await api.post("/progress/quiz-result", { lesson_id: lessonId, score, total });
    setProgress(data);
    return data;
  };

  const toggleWord = async (wordId) => {
    const { data } = await api.post("/progress/toggle-word", { word_id: wordId });
    setProgress(data);
    return data;
  };

  return (
    <AppContext.Provider
      value={{
        user,
        loadingAuth,
        progress,
        login,
        logout,
        setSession,
        refreshProgress,
        completeLesson,
        saveQuizResult,
        toggleWord,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
